<template>
  <div class="app-detail-page financial-page">
    <PageHeader
      :title="text.page.title"
      :subtitle="text.page.subtitle"
      :actions="headerActions"
      @action="onHeaderAction"
    />

    <div class="app-detail-content">
      <section class="metrics-grid app-detail-kpis">
        <CustomersKpiCard
          v-for="metric in kpis"
          :key="metric.key"
          :title="metric.title"
          :value="metric.value"
          :icon="metric.icon"
          :icon-bg="metric.iconBg"
          :icon-color="metric.iconColor"
        />
      </section>

      <AppSectionTabs
        v-model="activeTab"
        :tabs="tabs"
        :aria-label="text.page.tabsAriaLabel"
      />

      <FinancialSummaryTab
        v-if="activeTab === 'summary'"
        :text="text"
        :summary-cards="summaryCards"
        :show-add-card="shouldShowSummaryAddCard"
        :distribution="distribution"
        :movements="movements"
        @edit-account="handleEditAccount"
        @transfer-account="handleTransferAccount"
        @toggle-account-status="handleToggleAccountStatusFromMenu"
        @movements="openAccountMovements"
        @open-account="openAccountSummary"
      />

      <FinancialBankAccountsTab
        v-else-if="activeTab === 'bank-accounts'"
        :text="text"
        :accounts="bankAccounts"
        @edit-account="handleEditAccount"
        @transfer-account="handleTransferAccount"
        @toggle-account-status="handleToggleAccountStatusFromMenu"
        @movements="openAccountMovements"
        @open-account="openAccountSummary"
      />

      <FinancialConciliationTab
        v-else-if="activeTab === 'conciliation'"
        :text="text.conciliationTab"
      />

      <FinancialHistoryTab
        v-else-if="activeTab === 'history'"
        :text="text.historyTab"
      />

      <FinancialTransfersTab
        v-show="activeTab === 'transfers' || transferModalProxyOpen"
        :text="text"
        :open-create-signal="transferCreateSignal"
        @updated="handleTransfersUpdated"
        @modal-closed="handleTransferModalClosed"
      />

      <FinancialAccountModal
        :show="isCreateAccountModalOpen"
        :saving="createAccountSaving"
        @close="closeCreateAccountModal"
        @submit="handleCreateAccountSubmit"
      />

      <FinancialAccountModal
        mode="edit"
        :show="isEditAccountModalOpen"
        :saving="editAccountSaving"
        :initial-data="editAccountData"
        @close="closeEditAccountModal"
        @submit="handleEditAccountSubmit"
        @request-disable="handleRequestDisableAccount"
        @request-enable="handleRequestEnableAccount"
        @request-delete="handleRequestDeleteAccount"
      />

      <ConfirmActionModal
        :show="isConfirmActionModalOpen"
        :tone="confirmActionTone"
        :loading="confirmActionLoading"
        :title="confirmActionTitle"
        :subtitle="confirmActionSubtitle"
        :message="confirmActionMessage"
        :confirm-text="confirmActionButton"
        :cancel-text="text.modal.createAccount.actions.cancel"
        @confirm="confirmPendingAction"
        @cancel="closeConfirmActionModal"
      />
    </div>
  </div>
</template>

<script>
import PageHeader from '@/components/common/PageHeader.vue';
import CustomersKpiCard from '@/components/customers/CustomersKpiCard.vue';
import AppSectionTabs from '@/components/common/AppSectionTabs.vue';
import FinancialSummaryTab from '@/components/financial/tabs/FinancialSummaryTab.vue';
import FinancialBankAccountsTab from '@/components/financial/tabs/FinancialBankAccountsTab.vue';
import FinancialTransfersTab from '@/components/financial/tabs/FinancialTransfersTab.vue';
import FinancialConciliationTab from '@/components/financial/tabs/FinancialConciliationTab.vue';
import FinancialHistoryTab from '@/components/financial/tabs/FinancialHistoryTab.vue';
import FinancialAccountModal from '@/components/financial/modals/FinancialAccountModal.vue';
import ConfirmActionModal from '@/components/global/ConfirmActionModal.vue';
import { useDomainText } from '@/composables/text/useDomainText';
import financialAccountsService from '@/services/financialAccounts';
import { extractApiErrorMessage, extractApiSuccessMessage, extractPayload } from '@/utils/dataNormalization';
import { formatCurrency } from '@/utils/numberFormat';
import { formatDateTime } from '@/utils/datePresentation';

const { catalog: text } = useDomainText('financial');

const TYPE_CODE_BADGE_MAP = {
  CASH: 'cc',
  BANK: 'bb',
  CREDIT_CARD: 'sp',
  BANK_CREDIT: 'sv',
  NOT_BANK_CREDIT: 'usd',
};

const resolveBadgeCode = (row = {}) => {
  const typeCode = String(row?.type?.code || '').trim().toUpperCase();
  if (TYPE_CODE_BADGE_MAP[typeCode]) {
    return TYPE_CODE_BADGE_MAP[typeCode];
  }

  const currency = String(row?.currency || '').trim().toUpperCase();
  if (currency === 'USD') {
    return 'usd';
  }

  return 'san';
};

const formatCurrencyLabel = (amount, currency) => {
  const normalizedCurrency = String(currency || 'MXN').trim().toUpperCase() || 'MXN';
  return formatCurrency(amount, { currency: normalizedCurrency, maximumFractionDigits: 2 });
};

const formatLastSyncLabel = (value) => {
  return formatDateTime(value, { fallback: '', locale: 'es-MX' });
};

const mapAccountRowToUi = (row = {}, textCatalog = text) => {
  const isActive = String(row?.status || '').toLowerCase() !== 'inactive' && Number(row?.is_active ?? 0) === 1;
  const currency = String(row?.currency || 'MXN').trim().toUpperCase() || 'MXN';
  const bankName = String(row?.bank?.name || '').trim();
  const holder = String(row?.holder || '').trim();
  const branch = String(row?.bank?.branch || '').trim();
  const maskedNumber = String(row?.masked_account_number || '').trim();

  return {
    id: Number(row?.id || 0),
    code: resolveBadgeCode(row),
    bank: bankName,
    name: String(row?.name || '').trim() || textCatalog.summary.labels.account,
    meta: maskedNumber ? `${textCatalog.summary.labels.account} ${maskedNumber.replace(/\*/g, '•')}` : '',
    maskedNumber: maskedNumber.replace(/\*/g, '•'),
    owner: holder,
    branch,
    mode: 'available',
    balance: `${formatCurrencyLabel(row?.balance || 0, currency)} ${currency}`,
    currency,
    statusTone: isActive ? 'active' : 'inactive',
    statusLabel: isActive ? textCatalog.summary.status.active : textCatalog.summary.status.inactive,
    active: isActive,
    lastSync: formatLastSyncLabel(row?.last_sync_at),
  };
};

const mapMovementRowToUi = (movement = {}) => {
  const amount = Number(movement?.amount || 0);
  const category = String(movement?.category || '').trim();
  const accountMeta = String(movement?.account_meta || '').trim();

  return {
    id: Number(movement?.id || 0),
    financialAccountId: Number(movement?.financial_account_id || 0),
    date: String(movement?.date || '').trim(),
    time: String(movement?.time || '').trim() !== '' ? `${String(movement?.time || '').trim()} hrs` : '',
    account: String(movement?.account || '').trim(),
    accountMeta,
    concept: String(movement?.concept || '').trim(),
    category,
    categoryKey: String(movement?.category_key || movement?.type_reference || '').trim().toLowerCase(),
    typeReference: String(movement?.type_reference || '').trim().toLowerCase(),
    isIncome: Number(movement?.is_income || 0) === 1,
    amount,
    amountLabel: String(movement?.amountLabel || '').trim(),
  };
};

export default {
  name: 'FinancialPage',
  components: {
    PageHeader,
    CustomersKpiCard,
    AppSectionTabs,
    FinancialSummaryTab,
    FinancialBankAccountsTab,
    FinancialTransfersTab,
    FinancialConciliationTab,
    FinancialHistoryTab,
    FinancialAccountModal,
    ConfirmActionModal,
  },
  data() {
    return {
      text,
      activeTab: 'summary',
      isCreateAccountModalOpen: false,
      isEditAccountModalOpen: false,
      createAccountSaving: false,
      editAccountSaving: false,
      editAccountId: 0,
      editAccountData: null,
      isConfirmActionModalOpen: false,
      confirmActionLoading: false,
      pendingActionType: '',
      pendingActionAccount: null,
      headerActions: [
        {
          key: 'new-account',
          label: text.actions.header.newAccount,
          icon: 'fa-plus',
          variant: 'primary',
        },
        {
          key: 'transfer',
          label: text.actions.header.transfer,
          icon: 'fa-exchange',
          variant: 'secondary',
        },
      ],
      bankAccounts: [],
      summaryAccounts: [],
      movements: [],
      distribution: [],
      summaryKpis: {
        available_balance: 0,
        entries: 0,
        exits: 0,
        net_flow: 0,
        currency: 'MXN',
      },
      transferCreateSignal: 0,
      transferModalProxyOpen: false,
    };
  },
  created() {
    this.loadBankAccounts();
  },
  computed: {
    tabs() {
      return [
        { key: 'summary', label: this.text.tabs.summary },
        { key: 'bank-accounts', label: this.text.tabs.bankAccounts },
        { key: 'transfers', label: this.text.tabs.transfers },
        { key: 'conciliation', label: this.text.tabs.conciliation },
        { key: 'history', label: this.text.tabs.history },
      ];
    },
    kpis() {
      const currency = String(this.summaryKpis?.currency || 'MXN').trim().toUpperCase() || 'MXN';

      return [
        {
          key: 'available-balance',
          title: this.text.kpis.availableBalance,
          value: formatCurrencyLabel(Number(this.summaryKpis?.available_balance || 0), currency),
          icon: 'fa-bank',
          iconBg: '#e0f2fe',
          iconColor: '#0c4a6e',
        },
        {
          key: 'entries',
          title: this.text.kpis.entries,
          value: formatCurrencyLabel(Number(this.summaryKpis?.entries || 0), currency),
          icon: 'fa-arrow-up',
          iconBg: '#ecfdf5',
          iconColor: '#166534',
        },
        {
          key: 'exits',
          title: this.text.kpis.exits,
          value: formatCurrencyLabel(Number(this.summaryKpis?.exits || 0), currency),
          icon: 'fa-arrow-down',
          iconBg: '#fff1f2',
          iconColor: '#9f1239',
        },
        {
          key: 'net-flow',
          title: this.text.kpis.netFlow,
          value: formatCurrencyLabel(Number(this.summaryKpis?.net_flow || 0), currency),
          icon: 'fa-line-chart',
          iconBg: '#eff6ff',
          iconColor: '#1d4ed8',
        },
      ];
    },
    summaryCards() {
      return this.summaryAccounts.slice(0, 2);
    },
    shouldShowSummaryAddCard() {
      return this.summaryCards.length < 2;
    },
    confirmActionTitle() {
      if (this.pendingActionType === 'enable-account') {
        return this.text.modal.editAccount.confirm.enable.title;
      }

      if (this.pendingActionType === 'disable-account') {
        return this.text.modal.editAccount.confirm.disable.title;
      }

      return this.text.modal.editAccount.confirm.delete.title;
    },
    confirmActionSubtitle() {
      if (this.pendingActionType === 'enable-account') {
        return this.text.modal.editAccount.confirm.enable.subtitle;
      }

      if (this.pendingActionType === 'disable-account') {
        return this.text.modal.editAccount.confirm.disable.subtitle;
      }

      return this.text.modal.editAccount.confirm.delete.subtitle;
    },
    confirmActionMessage() {
      const accountName = String(this.pendingActionAccount?.name || this.editAccountData?.name || '').trim();
      const safeName = accountName || this.text.summary.labels.account;

      if (this.pendingActionType === 'enable-account') {
        return this.text.modal.editAccount.confirm.enable.message(safeName);
      }

      if (this.pendingActionType === 'disable-account') {
        return this.text.modal.editAccount.confirm.disable.message(safeName);
      }

      return this.text.modal.editAccount.confirm.delete.message(safeName);
    },
    confirmActionButton() {
      if (this.pendingActionType === 'enable-account') {
        return this.text.modal.editAccount.confirm.enable.confirm;
      }

      if (this.pendingActionType === 'disable-account') {
        return this.text.modal.editAccount.confirm.disable.confirm;
      }

      return this.text.modal.editAccount.confirm.delete.confirm;
    },
    confirmActionTone() {
      if (this.pendingActionType === 'delete-account') {
        return 'danger';
      }

      if (this.pendingActionType === 'enable-account') {
        return 'success';
      }

      return 'warning';
    },
  },
  methods: {
    async loadBankAccounts() {
      try {
        const [listResponse, summaryResponse] = await Promise.all([
          financialAccountsService.list(
            { status: 'all' },
            { skipLoading: true }
          ),
          financialAccountsService.summary(
            {},
            { skipLoading: true }
          ),
        ]);

        const listPayload = extractPayload(listResponse);
        const summaryPayload = extractPayload(summaryResponse);

        const rows = Array.isArray(listPayload?.rows)
          ? listPayload.rows
          : [];
        const summary = summaryPayload && typeof summaryPayload === 'object' ? summaryPayload : {};
        const kpis = summary?.kpis && typeof summary.kpis === 'object' ? summary.kpis : {};

        this.bankAccounts = rows.map((row) => mapAccountRowToUi(row, this.text));
        this.summaryKpis = {
          available_balance: Number(kpis?.available_balance || 0),
          entries: Number(kpis?.entries || 0),
          exits: Number(kpis?.exits || 0),
          net_flow: Number(kpis?.net_flow || 0),
          currency: String(kpis?.currency || 'MXN').trim().toUpperCase() || 'MXN',
        };

        const summaryCards = Array.isArray(summary?.cards) ? summary.cards : [];
        this.summaryAccounts = summaryCards.map((row) => mapAccountRowToUi(row, this.text));

        const distribution = Array.isArray(summary?.distribution) ? summary.distribution : [];
        if (distribution.length > 0) {
          this.distribution = distribution.map((item) => ({
            id: Number(item?.id || 0),
            name: String(item?.name || '').trim(),
            percent: Number(item?.percent || 0),
            percentLabel: String(item?.percentLabel || '').trim(),
            color: String(item?.color || '').trim() || '#64748b',
          }));
        } else {
          this.distribution = [];
        }

        const movements = Array.isArray(summary?.recent_movements) ? summary.recent_movements : [];
        if (movements.length > 0) {
          this.movements = movements.map((movement) => mapMovementRowToUi(movement));
        } else {
          this.movements = [];
        }
      } catch (error) {
        this.bankAccounts = [];
        this.summaryAccounts = [];
        this.movements = [];
        this.distribution = [];
        this.summaryKpis = {
          available_balance: 0,
          entries: 0,
          exits: 0,
          net_flow: 0,
          currency: 'MXN',
        };
      }
    },
    openCreateAccountModal() {
      this.isCreateAccountModalOpen = true;
    },
    closeCreateAccountModal() {
      if (this.createAccountSaving) {
        return;
      }

      this.isCreateAccountModalOpen = false;
    },
    closeEditAccountModal(force = false) {
      if (this.editAccountSaving && !force) {
        return;
      }

      this.isEditAccountModalOpen = false;
      this.editAccountId = 0;
      this.editAccountData = null;
    },
    async handleCreateAccountSubmit(payload) {
      if (this.createAccountSaving) {
        return;
      }

      this.createAccountSaving = true;

      try {
        const response = await financialAccountsService.store(payload);
        const apiErrorMessage = extractApiErrorMessage(response, '');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.$toast?.success(extractApiSuccessMessage(response, this.text.modal.createAccount.messages.createSuccess));
        this.isCreateAccountModalOpen = false;
        this.activeTab = 'bank-accounts';
        await this.loadBankAccounts();
      } catch (error) {
        const message = error?.response?.data?.message || this.text.modal.createAccount.messages.createError;
        this.$toast?.error(message);
      } finally {
        this.createAccountSaving = false;
      }
    },
    async handleEditAccount(account) {
      const accountId = Number(account?.id || 0);
      if (accountId <= 0 || this.editAccountSaving) {
        return;
      }

      try {
        const response = await financialAccountsService.show(accountId);
        this.editAccountData = extractPayload(response);
        this.editAccountId = accountId;
        this.isEditAccountModalOpen = true;
      } catch (error) {
        const message = error?.response?.data?.message || this.text.modal.editAccount.messages.loadError;
        this.$toast?.error(message);
      }
    },
    async handleEditAccountSubmit(payload) {
      if (this.editAccountSaving || this.editAccountId <= 0) {
        return;
      }

      this.editAccountSaving = true;

      try {
        const response = await financialAccountsService.update(this.editAccountId, payload || {});
        const apiErrorMessage = extractApiErrorMessage(response, '');
        if (apiErrorMessage) {
          this.$toast?.error(apiErrorMessage);
          return;
        }

        this.$toast?.success(extractApiSuccessMessage(response, this.text.modal.editAccount.messages.updateSuccess));
        this.closeEditAccountModal(true);
        await this.loadBankAccounts();
      } catch (error) {
        const message = error?.response?.data?.message || this.text.modal.editAccount.messages.updateError;
        this.$toast?.error(message);
      } finally {
        this.editAccountSaving = false;
      }
    },
    handleRequestDisableAccount() {
      if (!this.editAccountData?.id) {
        return;
      }

      this.openConfirmActionModal('disable-account', this.editAccountData);
    },
    handleRequestEnableAccount() {
      if (!this.editAccountData?.id) {
        return;
      }

      this.openConfirmActionModal('enable-account', this.editAccountData);
    },
    handleRequestDeleteAccount() {
      if (!this.editAccountData?.id) {
        return;
      }

      this.openConfirmActionModal('delete-account', this.editAccountData);
    },
    handleToggleAccountStatusFromMenu(account) {
      const accountId = Number(account?.id || 0);
      if (accountId <= 0) {
        return;
      }

      const isActive = Number(account?.active ? 1 : 0) === 1;
      this.openConfirmActionModal(isActive ? 'disable-account' : 'enable-account', account);
    },
    openConfirmActionModal(actionType, account) {
      this.pendingActionType = String(actionType || '').trim();
      this.pendingActionAccount = account || null;
      this.isConfirmActionModalOpen = true;
    },
    closeConfirmActionModal() {
      if (this.confirmActionLoading) {
        return;
      }

      this.isConfirmActionModalOpen = false;
      this.pendingActionType = '';
      this.pendingActionAccount = null;
    },
    async confirmPendingAction() {
      if (this.confirmActionLoading) {
        this.closeConfirmActionModal();
        return;
      }

      const accountId = Number(this.pendingActionAccount?.id || this.editAccountId || 0);
      if (accountId <= 0) {
        this.closeConfirmActionModal();
        return;
      }

      this.confirmActionLoading = true;
      let shouldCloseEditModal = false;

      try {
        if (this.pendingActionType === 'enable-account') {
          const payload = {
            is_active: 1,
          };
          const response = await financialAccountsService.toggleStatus(accountId, payload);
          this.$toast?.success(response?.message || this.text.modal.editAccount.messages.enableSuccess);
          shouldCloseEditModal = true;
        }

        if (this.pendingActionType === 'disable-account') {
          const payload = {
            is_active: 0,
          };
          const response = await financialAccountsService.toggleStatus(accountId, payload);
          this.$toast?.success(response?.message || this.text.modal.editAccount.messages.disableSuccess);
          shouldCloseEditModal = true;
        }

        if (this.pendingActionType === 'delete-account') {
          const response = await financialAccountsService.delete(accountId);
          this.$toast?.success(response?.message || this.text.modal.editAccount.messages.deleteSuccess);
          shouldCloseEditModal = true;
        }
      } catch (error) {
        const fallback = this.pendingActionType === 'delete-account'
          ? this.text.modal.editAccount.messages.deleteError
          : this.pendingActionType === 'enable-account'
            ? this.text.modal.editAccount.messages.enableError
            : this.text.modal.editAccount.messages.disableError;
        const message = error?.response?.data?.message || fallback || this.text.modal.editAccount.messages.actionError;
        this.$toast?.error(message);
      } finally {
        this.confirmActionLoading = false;
        this.closeConfirmActionModal();

        if (shouldCloseEditModal) {
          this.closeEditAccountModal();
          await this.loadBankAccounts();
        }
      }
    },
    onHeaderAction(action) {
      const key = String(action?.key || '').trim();

      if (!key) {
        return;
      }

      if (key === 'new-account') {
        this.openCreateAccountModal();
        return;
      }

      if (key === 'transfer') {
        this.transferModalProxyOpen = true;
        this.transferCreateSignal += 1;
        return;
      }
    },
    async handleTransfersUpdated(payload = {}) {
      this.transferModalProxyOpen = false;

      if (String(payload?.action || '').trim() === 'created') {
        this.activeTab = 'transfers';
      }

      await this.loadBankAccounts();
    },
    handleTransferAccount() {
      this.transferModalProxyOpen = true;
      this.transferCreateSignal += 1;
    },
    handleTransferModalClosed() {
      this.transferModalProxyOpen = false;
    },
    openAccountMovements(account) {
      const accountId = Number(account?.id || 0);
      if (accountId <= 0) {
        return;
      }

      this.$router.push({
        name: 'financialAccountDetail',
        params: { financialAccountId: String(accountId) },
        query: { tab: 'movements' },
      });
    },
    openAccountSummary(account) {
      const accountId = Number(account?.id || 0);
      if (accountId <= 0) {
        return;
      }

      this.$router.push({
        name: 'financialAccountDetail',
        params: { financialAccountId: String(accountId) },
        query: { tab: 'summary' },
      });
    },
  },
};
</script>

<style scoped>
.financial-page {
  gap: 0;
}
</style>
